import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { AlertTriangle, LoaderCircle } from "lucide-react";
import { BootstrapProvider, hasRole, useBootstrap, useBootstrapQuery } from "./lib/bootstrap";
import { Shell } from "./components/Shell";
import { Button } from "./components/ui";
import { useI18n } from "./lib/i18n";
const SchedulePage = lazy(() => import("./pages/SchedulePage").then((module) => ({ default: module.SchedulePage })));
const RequestsPage = lazy(() => import("./pages/RequestsPage").then((module) => ({ default: module.RequestsPage })));
const AssetsPage = lazy(() => import("./pages/AssetsPage").then((module) => ({ default: module.AssetsPage })));
const SpecificationsPage = lazy(() => import("./pages/SpecificationsPage").then((module) => ({ default: module.SpecificationsPage })));
const UsersPage = lazy(() => import("./pages/AdminPages").then((module) => ({ default: module.UsersPage })));
const AuditPage = lazy(() => import("./pages/AdminPages").then((module) => ({ default: module.AuditPage })));
const SecurityPage = lazy(() => import("./pages/SecurityPage").then((module) => ({ default: module.SecurityPage })));
const EmailTestPage = lazy(() => import("./pages/EmailTestPage").then((module) => ({ default: module.EmailTestPage })));
const ProfilePage = lazy(() => import("./pages/ProfilePage").then((module) => ({ default: module.ProfilePage })));
const LoginPage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.LoginPage })));
const ActivatePage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.ActivatePage })));
const ResetPasswordPage = lazy(() => import("./pages/AuthPages").then((module) => ({ default: module.ResetPasswordPage })));

function AppLayout() {
  const query = useBootstrapQuery();
  const { t } = useI18n();
  if (query.isLoading) {
    return (
      <div className="app-loading">
        <LoaderCircle className="spin" aria-hidden />
        <span>{t("app.loading")}</span>
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <div className="app-error">
        <AlertTriangle aria-hidden />
        <h1>{t("app.unavailable")}</h1>
        <p>{query.error instanceof Error ? query.error.message : t("app.loadFailed")}</p>
        <Button tone="primary" onPress={() => query.refetch()}>{t("app.retry")}</Button>
      </div>
    );
  }
  return (
    <BootstrapProvider value={query.data}>
      <Shell data={query.data}>
        <Outlet />
      </Shell>
    </BootstrapProvider>
  );
}

function LegacyReviewRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestId = params.get("review");
  if (requestId) {
    const token = params.get("token") || "";
    return <Navigate replace to={`/requests/${encodeURIComponent(requestId)}/review?token=${encodeURIComponent(token)}`} />;
  }
  return <Navigate replace to="/schedule" />;
}

function SuperadminOnly() {
  const data = useBootstrap();
  return hasRole(data.user.roles, "superadmin") ? <Outlet /> : <Navigate replace to="/schedule" />;
}

function AdminOnly() {
  const data = useBootstrap();
  return hasRole(data.user.roles, "admin", "superadmin") ? <Outlet /> : <Navigate replace to="/schedule" />;
}

export function App() {
  const { t } = useI18n();
  return (
    <Suspense fallback={<div className="app-loading"><LoaderCircle className="spin" /><span>{t("app.loading")}</span></div>}>
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login.html" element={<LoginPage />} />
      <Route path="/activate" element={<ActivatePage />} />
      <Route path="/activate.html" element={<ActivatePage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/reset-password.html" element={<ResetPasswordPage />} />
      <Route element={<AppLayout />}>
        <Route index element={<LegacyReviewRedirect />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/requests" element={<RequestsPage />} />
        <Route path="/requests/:id" element={<RequestsPage />} />
        <Route path="/requests/:id/review" element={<RequestsPage reviewMode />} />
        <Route element={<AdminOnly />}>
          <Route path="/assets" element={<AssetsPage />} />
          <Route path="/admin/specifications" element={<SpecificationsPage />} />
        </Route>
        <Route element={<SuperadminOnly />}>
          <Route path="/admin/users" element={<UsersPage />} />
          <Route path="/admin/audit" element={<AuditPage />} />
          <Route path="/admin/security" element={<SecurityPage />} />
          <Route path="/admin/email-test" element={<EmailTestPage />} />
        </Route>
        <Route path="/profile" element={<ProfilePage />} />
      </Route>
      <Route path="*" element={<Navigate replace to="/schedule" />} />
      </Routes>
    </Suspense>
  );
}
